// Player - bandit character with movement, animation, and stats

import { Vec2, CONST, clamp } from './utils.js';
import { BuffType } from './pickups.js';
import { flipSprite, whitePalette } from './renderer.js';

// Animation states
const Anim = {
  IDLE: 'idle',
  WALK: 'walk',
  ATTACK: 'attack',
};

// Pixel art sprite definitions for the bandit
// Each frame is an array of rows; palette maps chars to colors
const SPRITE_PALETTE = {
  'H': '#2c1810', // hat dark
  'h': '#3d2817', // hat
  'F': '#d4a574', // face/skin
  'E': '#1a1a1a', // eyes
  'B': '#1a1a1a', // bandana/mask
  'b': '#333',    // bandana dark
  'C': '#c0392b', // coat (red)
  'c': '#992d22', // coat dark
  'P': '#5a4a3a', // pants
  'p': '#4a3a2a', // pants dark
  'S': '#6b5b4b', // shoes
  'A': '#8a7a6a', // arms/hands
};

const SPRITE_IDLE = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' .BBB. ',
  ' CCCCC ',
  ' cCCCc ',
  ' .CCC. ',
  ' .PPP. ',
  ' pP.Pp ',
  ' SS.SS ',
];

const SPRITE_WALK_1 = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' .BBB. ',
  'ACCCCA ',
  ' cCCCc ',
  ' .CCC. ',
  ' .PPP. ',
  ' pP..P ',
  ' SS..S ',
];

const SPRITE_WALK_2 = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' .BBB. ',
  ' ACCCA ',
  ' cCCCc ',
  ' .CCC. ',
  ' .PPP. ',
  ' P..Pp ',
  ' S..SS ',
];

const SPRITE_ATTACK = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' .BBB. ',
  'ACCCCCAA',
  ' cCCCc  ',
  ' .CCC.  ',
  ' .PPP.  ',
  ' pP.Pp  ',
  ' SS.SS  ',
];

// Horse (side view, facing right; flipped with the robber). Two gallop
// frames; the robber is drawn on its back, HORSE_RIDER_LIFT px up.
const HORSE_PALETTE = {
  'M': '#2c1810', // mane / tail
  'd': '#8b5a2b', // coat
  'D': '#6b4220', // coat dark
  'e': '#1a1a1a', // eye
  'L': '#5a3a1a', // legs
  'h': '#222',    // hooves
};

const HORSE_RUN_1 = [
  '..........MM...',
  '.........MMdd..',
  '.........Mddedd',
  '.........ddd.DD',
  'M.dddddddddd...',
  'MMddddddddddd..',
  '.MDDDDDDDDDD...',
  '..L.L.....L.L..',
  '..L.L.....L.L..',
  '..h.h.....h.h..',
];

const HORSE_RUN_2 = [
  '..........MM...',
  '.........MMdd..',
  '.........Mddedd',
  '.........ddd.DD',
  'M.dddddddddd...',
  'MMddddddddddd..',
  '.MDDDDDDDDDD...',
  '.L..L.....L..L.',
  'L....L...L....L',
  'h....h...h....h',
];

const HORSE_RIDER_LIFT = 8;

export class Player {
  constructor(x, y) {
    this.pos = new Vec2(x, y);
    this.vel = new Vec2(0, 0);
    this.radius = CONST.PLAYER_RADIUS;

    // Stats
    this.maxHp = CONST.PLAYER_MAX_HP;
    this.hp = this.maxHp;
    this.damage = CONST.PLAYER_BASE_DAMAGE;
    this.speed = CONST.PLAYER_SPEED;
    this.attackRange = CONST.PLAYER_ATTACK_RANGE;
    this.attackCooldown = CONST.PLAYER_ATTACK_COOLDOWN;
    this.regenPerSec = CONST.PLAYER_HP_REGEN_PER_SEC;
    // HP actually restored, by source, since the last takeHealed().
    // Telemetry logs HP only on hits; this lets wave_end report the heals.
    this.healed = {};

    // Card-driven modifiers
    this.lifestealPct = 0;   // 0..1, heal % of dealt damage
    this.thornsPct = 0;      // 0..1, reflect % of incoming melee damage
    this.magnetRangeMul = 1; // 1 = default, cards scale up
    this.fullArcAttack = false; // true = attack hits in full 360° arc
    // v1.3.0 card additions:
    this.fortifyHealPerKill = 0; // HP healed when player kills a guard (fortify card)
    this.armorBreak = false;     // attacks ignore guard armor (armorBreak card, non-stackable)
    this.cleavePct = 0;          // 0..1, fraction of damage dealt to all enemies in arc beyond primary (cleave card)
    this.critChance = 0;         // 0..1, chance of double-damage attack (crit card)
    this.dashIframeOverride = 0; // seconds; if >0, replaces dash iframe duration (phantom card)
    // Rhythm card (v1.3.5, formerly doubleStrike): every Nth swing has half cooldown.
    // Stack 1 → [5], 2 → [5,7], 3 → [5,7,11]. attackCounter is the running
    // swing count used to test counter % N === 0 on each tryAttack().
    this.fastAttackTriggers = [];
    this.attackCounter = 0;

    // Resources
    this.gold = 0;

    // Movement
    this.accel = CONST.PLAYER_ACCEL;
    this.friction = CONST.PLAYER_FRICTION;
    this.facing = new Vec2(0, 1); // direction the player is facing

    // Animation
    this.anim = Anim.IDLE;
    this.animTimer = 0;
    this.animFrame = 0;
    this.walkFrames = [SPRITE_WALK_1, SPRITE_WALK_2];

    // Combat state
    this.attackTimer = 0;
    this.alive = true;
    this.regenAccum = 0;
    this.isAttacking = false;
    this.attackAnimTimer = 0;
    this.attackAnimDuration = 0.2; // seconds the attack anim plays

    // Dash state
    this.dashTimer = 0;            // counts down while dash is active
    this.dashCooldownTimer = 0;    // counts down until dash is ready again
    this.dashCooldownMax = CONST.PLAYER_DASH_COOLDOWN; // mutable via cards
    this.iframeTimer = 0;          // invulnerability window during dash
    this.iframeBonus = 0;          // added to dash iframe window via cards
    this.dashDir = new Vec2(1, 0); // locked direction while dashing

    // Knockback impulse (lancer charge). Added on top of normal movement
    // for PLAYER_KNOCKBACK_DURATION, decaying exponentially. Control is
    // never taken away — the player is shoved, not stunned.
    this.knockVel = new Vec2(0, 0);
    this.knockTimer = 0;

    // Sticky-arrow debuff: dash is locked out and top speed is cut while
    // `stickyTimer > 0`. Refreshing takes the longer of the two timers,
    // matching applyBurn / applyBuff.
    this.stickyTimer = 0;

    // Visual juice
    this.flashTimer = 0; // flash white on damage
    this.squashX = 1;    // squash/stretch scale
    this.squashY = 1;

    // Burn DoT state. Set by applyBurn(); ticked in update(). Damage
    // bypasses iframes (DoT, not a discrete hit) and skips lifesteal /
    // thorns. `burnDamageThisFrame` lets the game read what the burn
    // applied so it can route the loss through telemetry / floating text.
    this.burnTimer = 0;
    this.burnTickAccum = 0;
    this.burnDps = 0;
    this.burnDamageThisFrame = 0;

    // Timed buffs looted from guards (Fallout chem analogues — see
    // pickups.js). Map of BuffType → remaining seconds. The multiplier
    // fields below are derived from the active set by _recomputeBuffs()
    // and read at the relevant stat sites (combat damage, tryAttack
    // cooldown, takeDamage resistance).
    this.buffs = new Map();
    this.damageMul = 1;    // RAGE   — combat.js multiplies dealt damage
    this.attackCdMul = 1;  // FRENZY — tryAttack scales the cooldown
    this.damageResist = 0; // GUARD  — takeDamage ignores this fraction

    // Super strike meter (0..CONST.SUPER_CHARGE_MAX), filled by melee
    // damage dealt. Lives on the Player so it survives waves (respawnAt
    // keeps it) and resets with a new run or a Rebirth (new Player).
    this.superCharge = 0;

    // Horse (rare pickup): { hp, maxHp } while mounted, else null. Hits land
    // on its bar first (see _absorbByHorse). `horseAbsorbed` is what the
    // horse took on the last takeDamage call, `burnHorseThisFrame` what the
    // burn took this tick, `horseLost` a one-shot flag the caller consumes
    // via takeHorseLost() to log / show the horse running off.
    this.horse = null;
    this.horseAbsorbed = 0;
    this.burnHorseThisFrame = 0;
    this.horseLost = false;
  }

  get superReady() {
    return this.superCharge >= CONST.SUPER_CHARGE_MAX;
  }

  addSuperCharge(amount) {
    if (amount <= 0) return;
    this.superCharge = Math.min(CONST.SUPER_CHARGE_MAX, this.superCharge + amount);
  }

  // Spend a full meter. Returns true when the super fires; the caller
  // resolves the blast (combat.js performSuper).
  trySuper() {
    if (!this.alive || !this.superReady) return false;
    this.superCharge = 0;
    return true;
  }

  // Mount a horse with HORSE_HP_FRAC of the current max HP. No-op (false)
  // when already riding — the drop table also skips horses then.
  mountHorse() {
    if (!this.alive || this.horse) return false;
    const hp = Math.max(1, Math.round(this.maxHp * CONST.HORSE_HP_FRAC));
    this.horse = { hp, maxHp: hp };
    return true;
  }

  // Route `amount` through the horse; returns what's left for the robber.
  // The killing hit's overflow passes through.
  _absorbByHorse(amount) {
    if (!this.horse || amount <= 0) return amount;
    const taken = Math.min(this.horse.hp, amount);
    this.horse.hp -= taken;
    this.horseAbsorbed += taken;
    if (this.horse.hp <= 0) {
      this.horse = null;
      this.horseLost = true;
    }
    return amount - taken;
  }

  // True once after the horse's bar ran out; resets the flag.
  takeHorseLost() {
    const lost = this.horseLost;
    this.horseLost = false;
    return lost;
  }

  // Apply (or refresh) a timed buff. Refreshing extends to whichever
  // duration is longer rather than stacking, matching applyBurn.
  applyBuff(type, duration) {
    if (!type || duration <= 0) return;
    const cur = this.buffs.get(type) || 0;
    this.buffs.set(type, Math.max(cur, duration));
    this._recomputeBuffs();
  }

  // Derive the stat multipliers from the active buff set. Called whenever
  // the set changes (apply or expiry) so the read sites stay branch-free.
  _recomputeBuffs() {
    this.damageMul = this.buffs.has(BuffType.RAGE) ? CONST.BUFF_RAGE_DAMAGE_MUL : 1;
    this.attackCdMul = this.buffs.has(BuffType.FRENZY) ? CONST.BUFF_FRENZY_CD_MUL : 1;
    this.damageResist = this.buffs.has(BuffType.GUARD) ? CONST.BUFF_GUARD_RESIST : 0;
  }

  applyBurn(durationSec, dps) {
    if (durationSec <= 0 || dps <= 0) return;
    // Refreshing extends duration and bumps DPS to the stronger source.
    this.burnTimer = Math.max(this.burnTimer, durationSec);
    this.burnDps = Math.max(this.burnDps, dps);
  }

  // Glue the player down (sticky arrow). Blocks dash outright and scales
  // top speed by CONST.STICKY_SPEED_MUL until the timer runs out.
  applySticky(durationSec) {
    if (durationSec <= 0 || !this.alive) return;
    this.stickyTimer = Math.max(this.stickyTimer, durationSec);
  }

  get isStuck() {
    return this.stickyTimer > 0;
  }

  // Shove the player along (dirX, dirY) at `force` px/s. Decays over
  // CONST.PLAYER_KNOCKBACK_DURATION; a second hit overwrites rather than
  // stacks so a lancer pack can't launch the player across the map.
  applyKnockback(dirX, dirY, force) {
    if (!this.alive || force <= 0) return;
    const len = Math.sqrt(dirX * dirX + dirY * dirY);
    if (len < 0.0001) return;
    this.knockVel = new Vec2((dirX / len) * force, (dirY / len) * force);
    this.knockTimer = CONST.PLAYER_KNOCKBACK_DURATION;
  }

  update(dt, input, worldW, worldH) {
    if (!this.alive) return;

    // Tick dash timers first — dash state overrides normal movement.
    if (this.dashCooldownTimer > 0) this.dashCooldownTimer -= dt;
    if (this.dashTimer > 0) this.dashTimer -= dt;
    if (this.iframeTimer > 0) this.iframeTimer -= dt;
    if (this.stickyTimer > 0) this.stickyTimer = Math.max(0, this.stickyTimer - dt);

    const isDashing = this.dashTimer > 0;

    if (isDashing) {
      // Lock velocity and facing to the dash direction. Ignore input so the
      // dash can't be "cancelled" mid-air by WASD.
      this.vel = this.dashDir.mul(CONST.PLAYER_DASH_SPEED);
      this.facing = this.dashDir.copy();
    } else {
      // Normal acceleration/friction movement.
      const move = input.getMovement();
      const moveVec = new Vec2(move.x, move.y);
      // Sticky arrows cut the speed ceiling (and the acceleration that
      // reaches it) without touching the base stat, so the debuff can't
      // leak into card math or the shop stat readout.
      // The horse raises both the same way, so it stacks with the glue.
      const stuckMul = this.stickyTimer > 0 ? CONST.STICKY_SPEED_MUL : 1;
      const speedMul = stuckMul * (this.horse ? CONST.HORSE_SPEED_MUL : 1);
      const topSpeed = this.speed * speedMul;

      if (moveVec.lenSq() > 0) {
        const accelVec = moveVec.mul(this.accel * speedMul * dt);
        this.vel = this.vel.add(accelVec);
        this.vel = this.vel.clampLen(topSpeed);
        this.facing = moveVec.normalize();
      }

      const frictionMul = Math.exp(-this.friction * dt);
      this.vel = this.vel.mul(frictionMul);

      if (this.vel.lenSq() < 1) {
        this.vel.x = 0;
        this.vel.y = 0;
      }
    }

    // Update position
    this.pos = this.pos.add(this.vel.mul(dt));

    // Knockback rides on top of whatever the player is doing this frame.
    if (this.knockTimer > 0) {
      this.knockTimer -= dt;
      this.pos = this.pos.add(this.knockVel.mul(dt));
      this.knockVel = this.knockVel.mul(Math.exp(-CONST.PLAYER_KNOCKBACK_DECAY * dt));
      if (this.knockTimer <= 0) {
        this.knockTimer = 0;
        this.knockVel.set(0, 0);
      }
    }

    // Clamp to world boundaries
    this.pos.x = clamp(this.pos.x, this.radius, worldW - this.radius);
    this.pos.y = clamp(this.pos.y, this.radius, worldH - this.radius);

    // Update attack cooldown
    if (this.attackTimer > 0) {
      this.attackTimer -= dt;
    }

    // Passive health regeneration. HP is displayed as an integer, so we
    // accumulate fractional regen and apply it one whole point at a time.
    if (this.hp < this.maxHp) {
      this.regenAccum += this.regenPerSec * dt;
      if (this.regenAccum >= 1) {
        const whole = Math.floor(this.regenAccum);
        this.heal(whole, 'regen');
        this.regenAccum -= whole;
      }
    } else {
      this.regenAccum = 0;
    }

    // Burn DoT: tick down timer and apply integer damage as the
    // accumulator crosses each whole HP boundary. Bypasses iframes.
    // A mounted robber's horse burns first.
    this.burnDamageThisFrame = 0;
    this.burnHorseThisFrame = 0;
    if (this.burnTimer > 0 && this.alive) {
      this.burnTimer = Math.max(0, this.burnTimer - dt);
      this.burnTickAccum += this.burnDps * dt;
      if (this.burnTickAccum >= 1) {
        const whole = Math.floor(this.burnTickAccum);
        this.burnTickAccum -= whole;
        this.horseAbsorbed = 0;
        const rest = this._absorbByHorse(whole);
        this.burnHorseThisFrame = this.horseAbsorbed;
        this.horseAbsorbed = 0;
        this.hp = Math.max(0, this.hp - rest);
        this.burnDamageThisFrame = rest;
        if (this.hp <= 0) this.alive = false;
      }
      if (this.burnTimer <= 0) {
        this.burnTickAccum = 0;
        this.burnDps = 0;
      }
    }

    // Tick timed buffs; drop expired ones and recompute multipliers only
    // when the active set actually changes.
    if (this.buffs.size > 0) {
      let changed = false;
      for (const [type, remaining] of this.buffs) {
        const next = remaining - dt;
        if (next <= 0) {
          this.buffs.delete(type);
          changed = true;
        } else {
          this.buffs.set(type, next);
        }
      }
      if (changed) this._recomputeBuffs();
    }

    // Update animation
    this._updateAnimation(dt);
  }

  // Try to start a dash in the given direction. If the input vector is zero,
  // falls back to the current facing direction so a stationary player can
  // still dash forward. Returns true if the dash was started.
  startDash(dirX = 0, dirY = 0) {
    if (!this.alive) return false;
    if (this.dashCooldownTimer > 0 || this.dashTimer > 0) return false;
    // Sticky arrows glue the player to the ground — no dash escape.
    if (this.stickyTimer > 0) return false;

    const len = Math.sqrt(dirX * dirX + dirY * dirY);
    let dx, dy;
    if (len > 0.001) {
      dx = dirX / len;
      dy = dirY / len;
    } else {
      dx = this.facing.x;
      dy = this.facing.y;
    }
    this.dashDir = new Vec2(dx, dy);
    this.facing = this.dashDir.copy();
    this.dashTimer = CONST.PLAYER_DASH_DURATION;
    // Phantom card (v1.3.0): if dashIframeOverride > 0, use that as the
    // iframe duration (e.g. 1.0s) and add iframeBonus on top. Else use
    // the base dash iframe + bonus. Override > base ensures non-stackable
    // phantom is unambiguously stronger than stacked dashCooldown bonuses.
    const baseIframe = this.dashIframeOverride > 0
      ? this.dashIframeOverride
      : CONST.PLAYER_DASH_IFRAME_DURATION;
    this.iframeTimer = this.iframeDurationFor(baseIframe + this.iframeBonus);
    this.dashCooldownTimer = this.dashCooldownMax;
    return true;
  }

  // Clamp a requested i-frame window so it can never cover the whole dash
  // cooldown. Before this, phantom (1.0s) exactly matched the 1.0s base
  // cooldown — permanent invulnerability for anyone mashing dash — and one
  // dashCooldown stack pushed the ratio to 140%. Capping at
  // DASH_IFRAME_MAX_CD_FRACTION keeps a guaranteed vulnerable gap in every
  // dash cycle no matter how the build stacks, and keeps the cap scaling
  // with the cooldown so dashCooldown picks still shorten the gap in
  // absolute seconds without ever closing it.
  iframeDurationFor(requested) {
    const ceiling = this.dashCooldownMax * CONST.DASH_IFRAME_MAX_CD_FRACTION;
    return Math.min(requested, ceiling);
  }

  // Try to start an attack. Returns true if attack was initiated.
  tryAttack() {
    if (!this.alive || this.attackTimer > 0) return false;
    this.attackCounter++;
    // Rhythm trigger: if this swing's count hits any of the active Ns, the
    // resulting cooldown is halved (player can swing again twice as fast).
    const isFast = this.fastAttackTriggers.some(n => n > 0 && this.attackCounter % n === 0);
    // FRENZY buff scales the resulting cooldown on top of the Rhythm halving.
    const cd = isFast ? this.attackCooldown * 0.5 : this.attackCooldown;
    this.attackTimer = cd * this.attackCdMul;
    this.isAttacking = true;
    this.attackAnimTimer = this.attackAnimDuration;
    this.anim = 'attack';
    // Squash/stretch on attack: stretch horizontally in facing direction
    this.squashX = 1.3;
    this.squashY = 0.75;
    return true;
  }

  _updateAnimation(dt) {
    // Flash timer
    if (this.flashTimer > 0) {
      this.flashTimer -= dt;
    }

    // Squash/stretch: lerp back to 1
    this.squashX += (1 - this.squashX) * Math.min(1, 12 * dt);
    this.squashY += (1 - this.squashY) * Math.min(1, 12 * dt);

    // Attack animation takes priority
    if (this.attackAnimTimer > 0) {
      this.attackAnimTimer -= dt;
      this.anim = Anim.ATTACK;
      if (this.attackAnimTimer <= 0) {
        this.isAttacking = false;
      }
      return;
    }

    const moving = this.vel.lenSq() > 100; // threshold to switch to walk

    if (moving) {
      this.anim = Anim.WALK;
      this.animTimer += dt;
      // Walk cycle: switch frame every 0.15 seconds
      const frameDuration = 0.15;
      this.animFrame = Math.floor(this.animTimer / frameDuration) % this.walkFrames.length;
    } else {
      this.anim = Anim.IDLE;
      this.animTimer = 0;
      this.animFrame = 0;
    }
  }

  render(renderer) {
    if (!this.alive) return;

    const x = this.pos.x;
    const y = this.pos.y;

    // Choose sprite based on animation state
    let sprite;
    if (this.anim === Anim.ATTACK) {
      sprite = SPRITE_ATTACK;
    } else if (this.anim === Anim.WALK) {
      sprite = this.walkFrames[this.animFrame];
    } else {
      sprite = SPRITE_IDLE;
    }

    // Flip sprite horizontally if facing left
    const flipH = this.facing.x < -0.1;
    const drawSprite = flipH ? flipSprite(sprite) : sprite;

    // Apply squash/stretch transform
    const needsTransform = Math.abs(this.squashX - 1) > 0.01 || Math.abs(this.squashY - 1) > 0.01;
    if (needsTransform) {
      renderer.save();
      renderer.translate(x, y);
      renderer.scale(this.squashX, this.squashY);
      renderer.translate(-x, -y);
    }

    // See-through while invulnerable (plus the 🛡 timer in the HUD effects
    // row), so i-frames read at a glance even mid-dash.
    const iframes = this.iframeTimer > 0;
    if (iframes) renderer.setAlpha(0.4);

    // Mounted: horse under the robber, the robber lifted onto its back.
    let riderY = y;
    if (this.horse) {
      const horseSprite = this.anim === Anim.WALK && this.animFrame === 1 ? HORSE_RUN_2 : HORSE_RUN_1;
      const horsePalette = this.flashTimer > 0 ? whitePalette(HORSE_PALETTE) : HORSE_PALETTE;
      renderer.pixelSprite(x, y + 4, flipH ? flipSprite(horseSprite) : horseSprite, horsePalette, 2);
      riderY = y - HORSE_RIDER_LIFT;
    }

    // Flash white on damage
    if (this.flashTimer > 0) {
      renderer.pixelSprite(x, riderY, drawSprite, whitePalette(SPRITE_PALETTE), 2);
    } else {
      renderer.pixelSprite(x, riderY, drawSprite, SPRITE_PALETTE, 2);
    }

    if (iframes) renderer.resetAlpha();

    if (needsTransform) {
      renderer.restore();
    }

    // Sticky-arrow glue: a green puddle under the feet plus dripping
    // strands, so "why can't I dash?" is answerable at a glance.
    if (this.stickyTimer > 0) {
      renderer.setAlpha(0.5);
      renderer.circle(x, y + 9, 11, '#6b8e23');
      renderer.resetAlpha();
      renderer.setAlpha(0.75);
      renderer.rect(x - 7, y + 2, 3, 7, '#9acd32');
      renderer.rect(x + 4, y - 1, 3, 9, '#9acd32');
      renderer.resetAlpha();
    }

    // The horse's bar under its hooves, always shown: it never refills, so
    // the rider should see how much is left.
    if (this.horse) {
      renderer.healthBar(x - 14, y + 16, 28, 3, this.horse.hp / this.horse.maxHp, '#c08a4a', CONST.COLOR_HP_BG);
    }

    // Draw health bar above player if damaged
    if (this.hp < this.maxHp) {
      const barW = 24;
      const barH = 3;
      renderer.healthBar(
        x - barW / 2,
        riderY - 16,
        barW, barH,
        this.hp / this.maxHp,
        CONST.COLOR_HP_BAR,
        CONST.COLOR_HP_BG
      );
    }
  }

  // Turn the player to face the given direction vector. Zero-length vectors
  // are ignored so facing never degenerates to NaN.
  face(dx, dy) {
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > 0.0001) {
      this.facing = new Vec2(dx / len, dy / len);
    }
  }

  // `opts.ignoreIframes` is for area effects that are fields rather than
  // discrete hits (bomb blasts, the same way burn DoT already bypasses
  // i-frames). Damage resistance still applies. Returns the HP actually
  // lost so callers don't have to snapshot hp themselves; what the horse
  // soaked up is in `horseAbsorbed` (a hit "landed" when either is > 0 —
  // riders like burn and glue still apply to a mounted robber).
  takeDamage(amount, opts = {}) {
    this.horseAbsorbed = 0;
    if (!this.alive) return 0;
    // Dash i-frames: completely ignore incoming damage.
    if (this.iframeTimer > 0 && !opts.ignoreIframes) return 0;
    // Кураж (GUARD) buff: ignore a fraction of the hit. Round so HP stays
    // integer; only touched when the buff is active so the no-buff path is
    // byte-identical to before.
    if (this.damageResist > 0) {
      amount = Math.max(0, Math.round(amount * (1 - this.damageResist)));
    }
    amount = this._absorbByHorse(amount);
    const before = this.hp;
    this.hp -= amount;
    // Squash on hit
    this.squashX = 0.7;
    this.squashY = 1.3;
    // Reset regen accumulator so progress toward the next HP tick doesn't
    // carry over through damage.
    this.regenAccum = 0;
    if (this.hp <= 0) {
      this.hp = 0;
      this.alive = false;
    }
    return before - this.hp;
  }

  // Returns the HP actually restored (capped at maxHp).
  heal(amount, source = 'other') {
    const before = this.hp;
    this.hp = Math.min(this.maxHp, this.hp + amount);
    const restored = this.hp - before;
    if (restored > 0) this.healed[source] = (this.healed[source] || 0) + restored;
    return restored;
  }

  // Heals by source since the previous call; resets the counters.
  takeHealed() {
    const healed = this.healed;
    this.healed = {};
    return healed;
  }

  addGold(amount) {
    this.gold += amount;
  }

  reset(x, y) {
    this.pos.set(x, y);
    this.vel.set(0, 0);
    this.hp = this.maxHp;
    this.alive = true;
    this.attackTimer = 0;
    this.regenAccum = 0;
    this.dashTimer = 0;
    this.dashCooldownTimer = 0;
    this.iframeTimer = 0;
    this.anim = Anim.IDLE;
    this.animTimer = 0;
    this.animFrame = 0;
    this._clearBuffs();
    this._clearImpairments();
  }

  // Clear the movement-denial effects (sticky glue + knockback impulse).
  // Grouped so reset() and respawnAt() can't drift apart.
  _clearImpairments() {
    this.stickyTimer = 0;
    this.knockTimer = 0;
    this.knockVel.set(0, 0);
  }

  // Drop all active buffs and reset the derived multipliers.
  _clearBuffs() {
    this.buffs.clear();
    this.damageMul = 1;
    this.attackCdMul = 1;
    this.damageResist = 0;
  }

  // Move the player to a new spot between waves without touching HP, stats,
  // or upgrades. Clears motion / dash state so the spawn is clean.
  respawnAt(x, y) {
    this.pos.set(x, y);
    this.vel.set(0, 0);
    this.attackTimer = 0;
    this.dashTimer = 0;
    this.dashCooldownTimer = 0;
    this.iframeTimer = 0;
    this.anim = Anim.IDLE;
    this.animTimer = 0;
    this.animFrame = 0;
    this.isAttacking = false;
    this.attackAnimTimer = 0;
    this.squashX = 1;
    this.squashY = 1;
    this._clearBuffs();
    this._clearImpairments();
  }
}
